import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, X, ChevronLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Constantes ────────────────────────────────────────────────────────────────
const NODE_W = 180;
const NODE_H = 70;
const H_GAP  = 60;
const V_GAP  = 100;

// Couleurs par genre : 0=Féminin, 1=Masculin, 2=Inconnu, 3=Autre
const GENDER_COLORS = {
  0: { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  1: { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  2: { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' },
  3: { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' },
};
const genderColor = (g) => GENDER_COLORS[Number(g)] ?? GENDER_COLORS[2];

// ── Layout ────────────────────────────────────────────────────────────────────
// level négatif → y négatif → en HAUT  (aïeux)
// level 0       → y = 0     → au centre (racine)
// level positif → y positif → en BAS   (descendants)

function computeLayout(nodes) {
  const byLevel = {};
  nodes.forEach(n => {
    const l = n.level ?? 0;
    if (!byLevel[l]) byLevel[l] = [];
    byLevel[l].push(n);
  });

  const positions = {};
  Object.entries(byLevel).forEach(([level, group]) => {
    const totalW = group.length * NODE_W + (group.length - 1) * H_GAP;
    const startX = -totalW / 2;
    group.forEach((n, i) => {
      positions[n.handle] = {
        x: startX + i * (NODE_W + H_GAP),
        y: parseInt(level) * (NODE_H + V_GAP),   // négatif = haut, positif = bas
      };
    });
  });
  return positions;
}

// ── Nœud SVG ─────────────────────────────────────────────────────────────────
const PersonNode = ({ node, pos, isRoot, onClick }) => {
  const col  = genderColor(node.gender);
  const name = `${node.given_name || ''} ${node.surname || ''}`.trim() || '?';

  return (
    <g transform={`translate(${pos.x},${pos.y})`}
      onClick={() => onClick(node)} style={{ cursor: 'pointer' }}>
      {/* Ombre */}
      <rect x={3} y={3} width={NODE_W} height={NODE_H} rx={6} fill="rgba(0,0,0,0.08)" />
      {/* Fond */}
      <rect width={NODE_W} height={NODE_H} rx={6}
        fill={col.bg}
        stroke={isRoot ? '#f59e0b' : col.border}
        strokeWidth={isRoot ? 3 : 1.5} />
      {/* Bande couleur haute */}
      <rect width={NODE_W} height={6} rx={6} fill={col.border} />
      <rect width={NODE_W} height={3} y={3} fill={col.border} />
      {/* Nom */}
      <foreignObject x={8} y={12} width={NODE_W - 16} height={NODE_H - 20}>
        <div xmlns="http://www.w3.org/1999/xhtml" style={{
          fontFamily: 'Work Sans, sans-serif', fontSize: '12px', fontWeight: 600,
          color: col.text, lineHeight: '1.3', overflow: 'hidden',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {name}
        </div>
      </foreignObject>
      {/* Date de naissance */}
      {node.birth_date && (
        <text x={8} y={NODE_H - 8} fontSize={9} fill={col.text} opacity={0.7}
          fontFamily="Work Sans, sans-serif">
          🎂 {node.birth_date}
        </text>
      )}
      {/* Indicateur racine */}
      {isRoot && <circle cx={NODE_W - 10} cy={10} r={5} fill="#f59e0b" />}
    </g>
  );
};

// ── Edge SVG ──────────────────────────────────────────────────────────────────
const Edge = ({ from, to, positions, type }) => {
  const fp = positions[from];
  const tp = positions[to];
  if (!fp || !tp) return null;

  // Le point de départ est le bord du nœud le plus proche du nœud cible
  const fromBottom = fp.y < tp.y;   // la cible est plus bas → partir du bas du source
  const x1 = fp.x + NODE_W / 2;
  const y1 = fromBottom ? fp.y + NODE_H : fp.y;
  const x2 = tp.x + NODE_W / 2;
  const y2 = fromBottom ? tp.y : tp.y + NODE_H;

  const cy1 = y1 + (y2 - y1) * 0.4;
  const cy2 = y2 - (y2 - y1) * 0.4;

  const color = type === 'father' ? '#3b82f6'
              : type === 'mother' ? '#ec4899'
              : type === 'spouse' ? '#8b5cf6' : '#6b7280';
  // Traitement spécifique pour les conjoints : ligne horizontale sans flèche
  if (type === 'spouse') {
    const y = fp.y + NODE_H / 2;
    const sx = x1;
    const ex = x2;
    return (
      <line x1={sx} y1={y} x2={ex} y2={y}
        stroke={color} strokeWidth={2}
        strokeDasharray={'6,4'} opacity={0.9} />
    );
  }

  return (
    <path
      d={`M ${x1} ${y1} C ${x1} ${cy1}, ${x2} ${cy2}, ${x2} ${y2}`}
      fill="none" stroke={color} strokeWidth={1.8}
      strokeDasharray={type === 'child' ? '5,3' : 'none'}
      markerEnd={fromBottom ? 'url(#arrowDown)' : 'url(#arrowUp)'}
      opacity={0.75}
    />
  );
};

// ── SVG racine ────────────────────────────────────────────────────────────────
const TreeSVG = ({ nodes, edges, rootHandle, onNodeClick }) => {
  const positions = computeLayout(nodes);
  const xs = Object.values(positions).map(p => p.x);
  const ys = Object.values(positions).map(p => p.y);
  const PAD  = 50;
  const minX = Math.min(...xs) - PAD;
  const minY = Math.min(...ys) - PAD;
  const maxX = Math.max(...xs) + NODE_W + PAD;
  const maxY = Math.max(...ys) + NODE_H + PAD;

  return (
    <svg
      viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`}
      style={{ width: '100%', height: '100%' }}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        {/* Flèche vers le bas (enfant → descendant) */}
        <marker id="arrowDown" markerWidth="8" markerHeight="6"
          refX="7" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#6b7280" opacity="0.75" />
        </marker>
        {/* Flèche vers le haut (ascendant) */}
        <marker id="arrowUp" markerWidth="8" markerHeight="6"
          refX="1" refY="3" orient="auto">
          <polygon points="8 0, 0 3, 8 6" fill="#6b7280" opacity="0.75" />
        </marker>
      </defs>

      {/* Edges en dessous des nœuds */}
      <g>
        {edges.map((e, i) => (
          <Edge key={i} from={e.from} to={e.to}
            positions={positions} type={e.type} />
        ))}
      </g>
      {/* Nœuds au-dessus */}
      <g>
        {nodes.map(n => (
          <PersonNode key={n.handle} node={n}
            pos={positions[n.handle] || { x: 0, y: 0 }}
            isRoot={n.handle === rootHandle}
            onClick={onNodeClick} />
        ))}
      </g>
    </svg>
  );
};

// ── Fusion ascendants + descendants ──────────────────────────────────────────
// Le backend retourne déjà les bons niveaux :
//   ascendants  : 0, -1, -2…  (aïeux en négatif)
//   descendants : 0, +1, +2…  (enfants en positif)
// Il suffit de fusionner les deux sans transformation.

function mergeTreeData(ancestors, descendants) {
  const nodeMap = {};

  ancestors.nodes.forEach(n => { nodeMap[n.handle] = n; });
  descendants.nodes.forEach(n => {
    if (!nodeMap[n.handle]) nodeMap[n.handle] = n;
    // Si la racine est dans les deux, on garde level=0 (identique)
  });

  const edgeKeys = new Set();
  const edges = [];
  [...ancestors.edges, ...descendants.edges].forEach(e => {
    const key = `${e.from}|${e.to}|${e.type}`;
    if (!edgeKeys.has(key)) { edgeKeys.add(key); edges.push(e); }
  });

  return { nodes: Object.values(nodeMap), edges };
}

// ── Page principale ───────────────────────────────────────────────────────────
export const TreePage = () => {
  const navigate = useNavigate();

  const [rootHandle,  setRootHandle]  = useState('');
  const [searchTerm,  setSearchTerm]  = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg,    setShowSugg]    = useState(false);
  const [treeType,    setTreeType]    = useState('ancestors');
  const [nodes,       setNodes]       = useState([]);
  const [edges,       setEdges]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [history,     setHistory]     = useState([]);

  const searchRef = useRef(null);

  // ── Autocomplétion ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/persons`, { withCredentials: true });
        const q = searchTerm.toLowerCase();
        setSuggestions(
          data.filter(p =>
            `${p.given_name || ''} ${p.surname || ''}`.toLowerCase().includes(q)
          ).slice(0, 8)
        );
        setShowSugg(true);
      } catch { setSuggestions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const h = (e) => { if (!searchRef.current?.contains(e.target)) setShowSugg(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Chargement ──────────────────────────────────────────────────────────────
  const fetchTree = useCallback(async (handle, type) => {
    if (!handle) return;
    setLoading(true); setError('');
    try {
      if (type === 'both') {
        const [ancRes, descRes] = await Promise.all([
          axios.get(`${API_URL}/api/tree/ancestors/${handle}`,
            { withCredentials: true, params: { generations: 3 } }),
          axios.get(`${API_URL}/api/tree/descendants/${handle}`,
            { withCredentials: true, params: { generations: 3 } }),
        ]);
        const merged = mergeTreeData(ancRes.data, descRes.data);
        if (!merged.nodes.length) { setError('Aucun résultat.'); setNodes([]); setEdges([]); return; }
        setNodes(merged.nodes);
        setEdges(merged.edges);
      } else {
        const { data } = await axios.get(
          `${API_URL}/api/tree/${type}/${handle}`,
          { withCredentials: true, params: { generations: 4 } }
        );
        if (!data.nodes?.length) { setError('Aucun résultat.'); setNodes([]); setEdges([]); return; }
        setNodes(data.nodes);
        setEdges(data.edges);
      }
    } catch { setError("Erreur lors du chargement de l'arbre."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (rootHandle) fetchTree(rootHandle, treeType);
  }, [rootHandle, treeType, fetchTree]);

  // ── Interactions ────────────────────────────────────────────────────────────
  const selectPerson = (p) => {
    setSearchTerm(`${p.given_name || ''} ${p.surname || ''}`.trim());
    setRootHandle(p.handle);
    setHistory([]);
    setShowSugg(false);
  };

  const handleNodeClick = (node) => {
    if (node.handle === rootHandle) { navigate(`/persons/${node.handle}`); return; }
    setHistory(prev => [...prev, rootHandle]);
    setSearchTerm(`${node.given_name || ''} ${node.surname || ''}`.trim());
    setRootHandle(node.handle);
  };

  const goBack = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    const n = nodes.find(n => n.handle === prev);
    if (n) setSearchTerm(`${n.given_name || ''} ${n.surname || ''}`.trim());
    setRootHandle(prev);
  };

  const clearSearch = () => {
    setSearchTerm(''); setRootHandle('');
    setNodes([]); setEdges([]); setError(''); setHistory([]);
  };

  const rootNode  = nodes.find(n => n.handle === rootHandle);
  const TAB_TYPES = [
    { key: 'ancestors',   label: '↑ Ascendants' },
    { key: 'descendants', label: '↓ Descendants' },
    { key: 'both',        label: '↕ Vue complète' },
  ];

  return (
    <div data-testid="tree-page" className="space-y-4">
      <div>
        <h1 className="text-4xl font-bold mb-1"
          style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
          Arbre généalogique
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Aïeux en haut · Racine au centre · Descendants en bas
        </p>
      </div>

      {/* Recherche */}
      <div className="flex gap-2 items-start">
        {history.length > 0 && (
          <button onClick={goBack}
            className="flex items-center gap-1 px-3 py-2 text-sm border rounded-sm"
            style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)',
                     backgroundColor: 'var(--bg-card)' }}>
            <ChevronLeft size={16} /> Retour
          </button>
        )}
        <div className="relative flex-1" ref={searchRef}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-muted)' }} />
          <Input value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onFocus={() => suggestions.length && setShowSugg(true)}
            placeholder="Rechercher par nom ou prénom…"
            className="pl-9 pr-8" />
          {searchTerm && (
            <button onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--text-muted)' }}>
              <X size={15} />
            </button>
          )}
          {showSugg && suggestions.length > 0 && (
            <div className="absolute z-20 w-full mt-1 border shadow-lg max-h-60 overflow-y-auto"
              style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
              {suggestions.map(p => (
                <button key={p.handle} onClick={() => selectPerson(p)}
                  className="w-full text-left px-4 py-2.5 border-b last:border-0 text-sm"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <span className="font-medium" style={{ color: 'var(--forest)' }}>
                    {p.given_name} {p.surname}
                  </span>
                  {p.gramps_id && (
                    <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>
                      {p.gramps_id}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Onglets + légende */}
      <div className="flex gap-2 flex-wrap items-center">
        {TAB_TYPES.map(({ key, label }) => (
          <button key={key} onClick={() => setTreeType(key)}
            className="px-4 py-2 text-sm font-medium border rounded-sm transition-colors"
            style={{
              backgroundColor: treeType === key ? 'var(--forest)' : 'var(--bg-card)',
              color:           treeType === key ? '#fff' : 'var(--text-secondary)',
              borderColor:     treeType === key ? 'var(--forest)' : 'var(--border)',
            }}>
            {label}
          </button>
        ))}
        {nodes.length > 0 && (
          <div className="flex items-center gap-3 ml-auto text-xs flex-wrap"
            style={{ color: 'var(--text-muted)' }}>
            {[
              { color: '#3b82f6', label: 'Masculin' },
              { color: '#ec4899', label: 'Féminin' },
              { color: '#8b5cf6', label: 'Conjoint' },
              { color: '#f59e0b', label: 'Racine' },
            ].map(({ color, label }) => (
              <span key={label} className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full inline-block"
                  style={{ background: color }} />
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Zone arbre */}
      <div className="border overflow-auto"
        style={{ height: '65vh', minHeight: '500px',
                 backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border)' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full"
            style={{ color: 'var(--text-muted)' }}>
            Chargement{treeType === 'both' ? ' des deux arbres' : ''}…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full"
            style={{ color: 'var(--text-muted)' }}>{error}</div>
        ) : nodes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3"
            style={{ color: 'var(--text-muted)' }}>
            <Search size={36} style={{ opacity: 0.3 }} />
            <p>Recherchez une personne pour afficher son arbre</p>
          </div>
        ) : (
          <div style={{ width: '100%', height: '100%' }}>
            <TreeSVG nodes={nodes} edges={edges}
              rootHandle={rootHandle} onNodeClick={handleNodeClick} />
          </div>
        )}
      </div>

      {/* Infos bas de page */}
      {rootNode && (
        <div className="text-sm flex items-center gap-3 flex-wrap"
          style={{ color: 'var(--text-secondary)' }}>
          <span>
            Racine :
            <button onClick={() => navigate(`/persons/${rootHandle}`)}
              className="ml-1 font-semibold hover:underline"
              style={{ color: 'var(--forest)' }}>
              {rootNode.given_name} {rootNode.surname}
            </button>
          </span>
          <span style={{ color: 'var(--text-muted)' }}>·</span>
          <span>{nodes.length} personne{nodes.length > 1 ? 's' : ''}</span>
          <span style={{ color: 'var(--text-muted)' }}>·</span>
          <span>{edges.length} lien{edges.length > 1 ? 's' : ''}</span>
          <button onClick={() => navigate(`/persons/${rootHandle}`)}
            className="ml-auto text-xs font-medium hover:underline"
            style={{ color: 'var(--terracotta)' }}>
            Voir la fiche →
          </button>
        </div>
      )}
    </div>
  );
};
